import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/useAuth";
import { Mail, Lock, AlertCircle, Eye, EyeOff, CheckCircle } from "lucide-react";
import { ThemeLoader } from "../../shared/components/ThemeLoader";
import { OrchyLogo } from "../ui/OrchyLogo";
import { SEO } from "../SEO";

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { login, loginWithGoogle, isLoading } = useAuth();

  // Apply the persisted theme (dark/light) so the login page matches the rest of the app
  useEffect(() => {
    const saved = window.localStorage.getItem("orchy-theme") ?? window.localStorage.getItem("af-theme");
    const prefersDark = window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
    const dark = saved === "dark" || ((saved === null || saved === "system") && prefersDark);
    window.document.documentElement.classList.toggle("dark", dark);
  }, []);
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  
  // Refs to store values that need to persist across async operations
  const errorRef = useRef("");
  const formDataRef = useRef({ email: "", password: "" });
  const updateCountRef = useRef(0);

  // Check localStorage for state updates and apply them
  useEffect(() => {
    const checkForUpdates = () => {
      const storedError = localStorage.getItem("loginError");
      if (storedError) {
        console.log("Detected error in localStorage:", storedError);
        setError(storedError);
        
        const storedFormData = localStorage.getItem("loginFormData");
        if (storedFormData) {
          try {
            const data = JSON.parse(storedFormData);
            console.log("Detected formData in localStorage:", data);
            setFormData(data);
            formDataRef.current = data;
          } catch (e) {
            console.error("Error parsing formData:", e);
          }
        }
        
        localStorage.removeItem("loginError");
        localStorage.removeItem("loginFormData");
      }
    };

    // Poll more frequently initially (50ms) to catch quick updates
    // This handles the React 18 batching issue
    const interval = setInterval(checkForUpdates, 50);
    return () => clearInterval(interval);
  }, []);
  useEffect(() => {
    if (searchParams.get("signup") === "success") {
      setShowSuccessMessage(true);
      const timer = setTimeout(() => {
        setShowSuccessMessage(false);
        setSearchParams({});
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [searchParams.get("signup")]);



  const validateEmail = (email: string): string | null => {
    if (!email || !email.trim()) return "Email is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "Please enter a valid email address";
    return null;
  };

  const validatePassword = (password: string): string | null => {
    if (!password || !password.trim()) return "Password is required";
    if (password.length < 1) return "Password cannot be empty";
    return null;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    formDataRef.current = { ...formDataRef.current, [name]: value };
    
    // Clear error when user starts typing
    if (error) {
      setError("");
      localStorage.removeItem("loginError");
    }
    
    // Real-time validation
    if (name === "email") {
      const error = validateEmail(value);
      setFieldErrors(prev => ({ ...prev, email: error || undefined }));
    } else if (name === "password") {
      const error = validatePassword(value);
      setFieldErrors(prev => ({ ...prev, password: error || undefined }));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isSubmitting && !isLoading) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    // Store form data before any state updates
    const emailToLogin = formData.email;
    const passwordToLogin = formData.password;
    
    // Only clear field errors at start of new submission
    setFieldErrors({});

    // Step 1: Validate email format and presence
    const emailError = validateEmail(emailToLogin);
    if (emailError) {
      setError("");
      setFieldErrors({ email: emailError });
      return;
    }

    // Step 2: Validate password presence and format
    const passwordError = validatePassword(passwordToLogin);
    if (passwordError) {
      setError("");
      setFieldErrors({ password: passwordError });
      return;
    }

    // Step 3: All validations passed, attempt login
    setIsSubmitting(true);

    try {
      await login(emailToLogin, passwordToLogin);
      navigate("/chat");
    } catch (err: any) {
      console.log("Caught error:", err);
      const errorMessage = err.message || "Login failed. Please try again.";
      console.log("Setting error message:", errorMessage);
      
      // Store error and form data in localStorage for the polling effect to detect
      // This bypasses React 18 automatic batching issues
      localStorage.setItem("loginError", errorMessage);
      localStorage.setItem("loginFormData", JSON.stringify({ email: emailToLogin, password: passwordToLogin }));
      
      // Also try direct setState (may or may not work due to batching)
      setError(errorMessage);
      setFormData({ email: emailToLogin, password: passwordToLogin });
      
      setIsSubmitting(false);
    } finally {
      // Cleanup if needed
    }
  };

  return (
    <div className="min-h-screen bg-base flex items-center justify-center p-4">
      <SEO title="Sign In" description="Sign in to your Orchy account to access the ADK runtime observability and control plane." noIndex />
      {/* Loading Overlay */}
      {(isSubmitting || isLoading) && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-surface rounded-lg border border-edge shadow-lg p-8 flex flex-col items-center gap-4">
            <ThemeLoader size={32} />
            <p className="text-text font-medium">Signing in...</p>
          </div>
        </div>
      )}

      <main id="main-content" className="w-full max-w-md">
        {/* Logo / Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <OrchyLogo size={28} />
            <h1 className="text-3xl font-bold text-text">Orchy</h1>
          </div>
          <p className="text-secondary">Workflow Orchestration Platform</p>
        </div>

        {/* Login Card */}
        <div className="bg-surface rounded-lg border border-edge shadow-lg p-8">
          <h2 className="text-2xl font-bold text-text mb-6">Sign In</h2>

          {/* Error Message */}
          {error && (
            <div role="alert" aria-live="assertive" className="mb-4 p-3 bg-error/10 border border-error rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-error flex-shrink-0 mt-0.5" aria-hidden="true" />
              <p className="text-error text-sm">{error}</p>
            </div>
          )}

          {/* Success Message */}
          {showSuccessMessage && (
            <div role="status" aria-live="polite" className="mb-4 p-3 bg-brand/10 border border-brand rounded-lg flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-brand flex-shrink-0 mt-0.5" aria-hidden="true" />
              <div>
                <p className="text-brand text-sm font-medium">Account created successfully!</p>
                <p className="text-brand text-xs mt-1">Please sign in with your credentials.</p>
              </div>
            </div>
          )}

          {/* Form */}
          <form className="space-y-5" onSubmit={e => { e.preventDefault(); void handleSubmit(); }} noValidate>
            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-text mb-2">
                Email <span className="text-error" aria-hidden="true">*</span>
                <span className="sr-only">(required)</span>
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 w-5 h-5 text-secondary" aria-hidden="true" />
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  placeholder="you@example.com"
                  disabled={isSubmitting}
                  autoComplete="email"
                  aria-required="true"
                  aria-invalid={!!fieldErrors.email}
                  aria-describedby={fieldErrors.email ? "email-error" : undefined}
                  className={`w-full bg-base border rounded-lg pl-10 pr-4 py-2.5 text-text placeholder-secondary focus:outline-none focus:ring-1 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed transition ${
                    fieldErrors.email
                      ? "border-error focus:ring-error"
                      : "border-edge focus:ring-brand"
                  }`}
                />
              </div>
              {fieldErrors.email && (
                <p id="email-error" role="alert" className="text-xs text-error mt-1">{fieldErrors.email}</p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-text mb-2">
                Password <span className="text-error" aria-hidden="true">*</span>
                <span className="sr-only">(required)</span>
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 w-5 h-5 text-secondary" aria-hidden="true" />
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  placeholder="••••••••"
                  disabled={isSubmitting}
                  autoComplete="current-password"
                  aria-required="true"
                  aria-invalid={!!fieldErrors.password}
                  aria-describedby={fieldErrors.password ? "password-error" : undefined}
                  className={`w-full bg-base border rounded-lg pl-10 pr-10 py-2.5 text-text placeholder-secondary focus:outline-none focus:ring-1 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed transition ${
                    fieldErrors.password
                      ? "border-error focus:ring-error"
                      : "border-edge focus:ring-brand"
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isSubmitting}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-3 top-3 text-secondary hover:text-text disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" aria-hidden="true" /> : <Eye className="w-5 h-5" aria-hidden="true" />}
                </button>
              </div>
              {fieldErrors.password && (
                <p id="password-error" role="alert" className="text-xs text-error mt-1">{fieldErrors.password}</p>
              )}
            </div>

            {/* Forgot Password Link */}
            <div className="flex justify-end mb-4">
              <Link
                to="/forgot-password"
                className="text-xs text-brand hover:text-brand-light transition font-medium"
              >
                Forgot password?
              </Link>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting || isLoading}
              className="w-full bg-brand hover:bg-brand-dim disabled:bg-edge disabled:text-secondary text-on-primary font-semibold py-2.5 rounded-lg transition flex items-center justify-center gap-2 mt-6"
            >
              {(isSubmitting || isLoading) && <ThemeLoader size={16} />}
              {isSubmitting || isLoading ? "Signing in..." : "Sign In"}
            </button>
          </form>

          {/* Divider */}
          <div className="my-5 relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-edge" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-2 bg-surface text-secondary">or continue with</span>
            </div>
          </div>

          {/* Google Sign-In */}
          <button
            type="button"
            onClick={async () => {
              try {
                await loginWithGoogle();
                // Navigation handled by App.tsx isAuthenticated effect
              } catch (err: any) {
                setError(err.message || "Google sign-in failed.");
              }
            }}
            disabled={isSubmitting || isLoading}
            className="w-full flex items-center justify-center gap-3 py-2.5 px-4 rounded-lg border border-edge bg-surface hover:bg-card-hover disabled:opacity-50 disabled:cursor-not-allowed transition font-semibold text-sm text-text"
          >
            {/* Google logo SVG */}
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Sign in with Google
          </button>

          {/* Sign Up Link */}
          <p className="mt-4 text-center text-secondary hover:text-text transition">
            Don't have an account?{" "}
            <Link
              to="/signup"
              className="text-brand hover:text-brand-light dark:hover:text-brand-light hover:underline font-semibold transition"
            >
              Sign up
            </Link>
          </p>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-secondary text-sm">
          <p>&copy; {new Date().getFullYear()} Orchy. All rights reserved.</p>
        </div>
      </main>
    </div>
  );
};
